/** Scripture selected by hand and copied (the browser's own Copy, Ctrl+C or a
 *  long press over the words) leaves in quotation marks, nested the same way
 *  every other copy nests them (`quoteScripture`). Before this the browser
 *  copied the bare words, verse numbers and footnote marks included, while
 *  the selection bar's Copy quoted them (owner, 2026-10-21: "When copying
 *  scripture, it should include quotations").
 *
 *  Only a selection that starts and ends in a verse is taken, and only the
 *  words of each verse (`.verse-text`), so a verse number or a footnote mark
 *  the drag passed over is not copied as if it were Scripture. Anything else
 *  (a heading, a note, a field) is the browser's to copy as it always was. */
import { quoteScripture } from '../services/bible/citation';

const elementOf = (n: Node): Element | null => (n instanceof Element ? n : n.parentElement);

/** The words of the verses a selection covers, or '' when it is not all
 *  inside Scripture. */
export function selectedScripture(sel: Selection): string {
  const parts: string[] = [];
  for (let i = 0; i < sel.rangeCount; i++) {
    const r = sel.getRangeAt(i);
    const host = elementOf(r.commonAncestorContainer);
    if (!host || host.closest('input, textarea, [contenteditable="true"]')) return '';
    if (!elementOf(r.startContainer)?.closest('.verse') || !elementOf(r.endContainer)?.closest('.verse')) return '';
    const own = host.closest('.verse-text');
    const words = own ? [own] : [...host.querySelectorAll('.verse-text')].filter((n) => r.intersectsNode(n));
    for (const w of words) {
      const part = document.createRange();
      part.selectNodeContents(w);
      if (r.compareBoundaryPoints(Range.START_TO_START, part) > 0) part.setStart(r.startContainer, r.startOffset);
      if (r.compareBoundaryPoints(Range.END_TO_END, part) < 0) part.setEnd(r.endContainer, r.endOffset);
      const s = part.toString().replace(/\s+/g, ' ').trim();
      if (s) parts.push(s);
    }
  }
  return parts.join(' ');
}

let installed = false;

export function installScriptureCopy(): void {
  if (installed) return;
  installed = true;
  document.addEventListener('copy', (e) => {
    const sel = document.getSelection();
    if (!sel || sel.isCollapsed || !e.clipboardData) return;
    const words = selectedScripture(sel);
    if (!words) return;
    e.clipboardData.setData('text/plain', quoteScripture(words));
    e.preventDefault();
  });
}
